import { useEffect, useMemo, useRef, useState } from 'react';

import { useLocation } from 'react-router';

import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { isOptimizelyEnabled } from './enabled';
import { injectScripts } from './injectScripts';

type Props = Readonly<{
	allowed: boolean;
	trackingAllowed?: boolean;
	locationMatch?: ReadonlyArray<RegExp | string>;
}>;

const normalizeRegExp = (value: RegExp | string) => {
	if (value instanceof RegExp) return value;
	try {
		// eslint-disable-next-line security/detect-non-literal-regexp
		return new RegExp(value);
	} catch (e) {
		return undefined;
	}
};

export function Optimizely({ allowed, trackingAllowed, locationMatch }: Props): null {
	const { pathname } = useLocation();

	const locationMatchRegExp = useMemo(
		() => locationMatch?.map(normalizeRegExp).filter((value): value is RegExp => !!value),
		[locationMatch],
	);

	// initialize for first render for SSR support
	const [enabled, setEnabled] = useState(() =>
		isOptimizelyEnabled({ allowed, locationMatch: locationMatchRegExp, pathname }),
	);

	const { optimizely } = useUtilityContext();

	const scriptsRef = useRef<HTMLScriptElement[]>([]);

	useEffect(() => {
		// once it has been enabled once, leave it enabled
		// eslint-disable-next-line react-hooks/set-state-in-effect
		setEnabled(
			(alreadyEnabled) =>
				alreadyEnabled || isOptimizelyEnabled({ allowed, locationMatch: locationMatchRegExp, pathname }),
		);
	}, [setEnabled, allowed, locationMatchRegExp, pathname]);

	useEffect(() => {
		if (enabled) {
			optimizely.setState(trackingAllowed ? 'enabled' : 'opted-out');
		} else {
			optimizely.setState('disabled');
		}
	}, [optimizely, enabled, trackingAllowed]);

	useEffect(() => {
		// Remove previously injected scripts
		for (const script of scriptsRef.current) {
			script.remove();
		}
		scriptsRef.current = [];

		if (enabled) {
			scriptsRef.current = injectScripts(optimizely, !!trackingAllowed);
		}

		return () => {
			for (const script of scriptsRef.current) {
				script.remove();
			}
			scriptsRef.current = [];
		};
	}, [enabled, trackingAllowed, optimizely]);

	return null;
}
